import React, { useEffect, useRef, useState } from "react";
import i18n from "i18next";
import VisorChrome from "./VisorChrome";
import { seekZone } from "./chromeState";

const DOUBLE_TAP_MS = 300;
const FEEDBACK_MS = 600;
const SEEK_STEP_SEC = 10;
// Ghost-click window: after a real touch tap, browsers synthesize a mousedown
// (usually ~300ms later, longer on slow devices). Counting that echo would
// turn every single mobile tap into a double-tap seek.
const GHOST_CLICK_MS = 700;

// Mobile spec B3 "ripple zones" / desktop DK8 shortcut legend: double-tap the
// left/right third of the stage to seek +-10s.
//
// STACKING (task-8 critical constraint): mounted by VisorCanvasSli INSIDE
// #maincontent ("innercanvas"), between the stage-background div and the
// boxes.map() list -- NOT as a VisorApp-level sibling of <Container> the way
// SceneEndCta/SegmentBar are. A VisorApp-level overlay can't satisfy "above
// background, below boxes": per CSS2.1 Appendix E, a positioned sibling of
// <Container> with any explicit z-index paints as one indivisible unit
// either fully above or fully below <Container>'s whole subtree -- it can
// never interleave with only PART of its descendants. Nesting inside
// #maincontent instead puts this overlay in the SAME painting bucket as the
// boxes (step 6: position:absolute descendants with z-index auto/0, ordered
// by DOM tree order): the background div (z-index:0) paints first, this
// overlay paints next, the boxes (position:absolute, z-index:auto, later in
// the DOM) paint last. No numeric z-index of our own -- adding one would
// move us into a different painting step and defeat the DOM-order trick.
// See task-8-report.md for the full trace this comment summarizes.
//
// GEOMETRY: #maincontent's own CSS `position` is ambiguous in the visor
// bundle (the editor's `.sli { position: relative }` rule lives in an
// editor-only scss file that visor's webpack entry never imports), so this
// overlay's technical containing block may resolve to a distant, larger
// ancestor (#page-content-wrapper) instead of the stage itself. Rather than
// add `position: relative` to the shared `.sli`/`.innercanvas` selector --
// which every box also positions against, so touching it risks reflowing
// every box on every scene across the whole product -- this component
// sidesteps the question:
//   - #maincontent already has `overflow: hidden` (pre-existing rule), so
//     even an oversized/mis-based absolute box is visually clipped to
//     exactly the stage's rect -- the interactive area is correct
//     regardless of the technical containing block.
//   - the tap handler reads geometry from the overlay's OWN parentElement
//     (== #maincontent, guaranteed by where this component is mounted)
//     rather than from the overlay's own possibly-oversized
//     getBoundingClientRect(), so the left/right third math is always
//     computed against the real, visible stage width.
export default function StageSeekZones({ show, isMandatory, elapsed = 0, onSeekTo }) {
  const overlayRef = useRef(null);
  const lastTapRef = useRef({ zone: null, time: 0 });
  const lastTouchAtRef = useRef(0);
  const [feedback, setFeedback] = useState(null);
  const feedbackTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(feedbackTimerRef.current), []);

  if (!show || isMandatory) return null;

  const handleTap = (e, clientX) => {
    const el = overlayRef.current;
    // Boxes/plugins sit above us in paint order, so a tap on one never
    // reaches this handler at all; this check only guards against taps that
    // bubble up from OUR OWN children (the ripple feedback), not real box
    // passthrough.
    if (!el || e.target !== el) return;
    const stage = el.parentElement || el;
    const rect = stage.getBoundingClientRect();
    const zone = seekZone(clientX - rect.left, rect.width);
    if (!zone) return;

    const now = Date.now();
    const last = lastTapRef.current;
    if (last.zone === zone && now - last.time < DOUBLE_TAP_MS) {
      lastTapRef.current = { zone: null, time: 0 };
      onSeekTo && onSeekTo(zone === "back" ? elapsed - SEEK_STEP_SEC : elapsed + SEEK_STEP_SEC);
      setFeedback(zone);
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = setTimeout(() => setFeedback(null), FEEDBACK_MS);
    } else {
      lastTapRef.current = { zone, time: now };
    }
  };

  return (
    <VisorChrome>
      <div
        ref={overlayRef}
        className="vchrome-seekzones"
        onMouseDown={(e) => {
          // Touch-precedence dedupe: ignore the mousedown a browser
          // synthesizes after a touch tap (GHOST_CLICK_MS above). Never
          // preventDefault on touchstart instead — that would suppress
          // scrolling/click synthesis for anything under the stage.
          if (Date.now() - lastTouchAtRef.current < GHOST_CLICK_MS) return;
          handleTap(e, e.clientX);
        }}
        onTouchStart={(e) => {
          lastTouchAtRef.current = Date.now();
          const t = e.touches[0];
          t && handleTap(e, t.clientX);
        }}
      >
        {feedback && (
          <div
            className={`vchrome-seek-ripple vchrome-seek-ripple--${feedback}`}
            aria-hidden="true"
          >
            <span>{i18n.t(feedback === "back" ? "VisorSeekBack" : "VisorSeekFwd")}</span>
          </div>
        )}
      </div>
    </VisorChrome>
  );
}
